import { useRef, type ReactNode } from 'react';
import { Icon } from '@forecastify/ui';
import { useT } from '../i18n';
import { useFileDrop } from './file-drop';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ "Drop a file here — or browse." One area, not a button.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **AN AREA RATHER THAN A BUTTON, WHICH IS THE WHOLE OF ITEM 5.** Every upload in the product
 * was a button that opened the operating system's file dialogue — so somebody with the file already
 * on screen had to go and find it again through a tree. The drop target is the affordance; the
 * browse link stays for the cases where a file is not to hand.
 *
 * ⚠️ **`file-drop.ts` does the hard part**, and its header lists the four ways a hand-written drop
 * goes wrong — three of them silently. This is the words and the box; that is the mechanism.
 *
 * ⚠️ **`disabled` still swallows the drop.** Letting the document have it navigates the tab to the
 * file, which loses whatever was on the screen — the hook says so at greater length.
 */
export function DropZone({
  accept,
  disabled = false,
  busy = false,
  onFiles,
  children,
  className,
}: {
  /** ⚠️ Passed to the input AND used as the hint — the two must not disagree. */
  readonly accept: readonly string[];
  readonly disabled?: boolean;
  /** ⚠️ Distinct from `disabled`: an upload in flight still accepts nothing, but says why. */
  readonly busy?: boolean;
  readonly onFiles: (files: readonly File[]) => void;
  /** Anything to draw above the prompt — a preview, an avatar. */
  readonly children?: ReactNode;
  readonly className?: string;
}) {
  const t = useT();
  const picker = useRef<HTMLInputElement | null>(null);
  const { over, handlers } = useFileDrop({
    enabled: !disabled && !busy,
    onFiles,
  });

  return (
    <div
      {...handlers}
      className={[
        'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-5 text-center transition-colors',
        over ? 'border-primary bg-primary/5' : 'border-input',
        disabled || busy ? 'opacity-60' : '',
        className ?? '',
      ].join(' ')}
    >
      {children}

      <input
        ref={picker}
        type="file"
        accept={accept.join(',')}
        className="hidden"
        disabled={disabled || busy}
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          /* ⚠️ Cleared, so choosing the same file twice still fires `change` — otherwise a failed
             upload cannot be retried with the same file, which is the commonest retry there is. */
          event.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />

      <p className="text-[12px]">
        <Icon name="upload" size={14} className="mr-1.5 inline-block align-text-bottom" />
        {busy ? t('upload.busy') : t('upload.drop')}
      </p>

      {/**
       * ⚠️⚠️ **THE FORMATS IT WILL ACCEPT, DERIVED FROM `accept` RATHER THAN WRITTEN OUT.** Asked
       * for after testing, and the derivation is what keeps it honest: the hint and the file
       * picker's own filter cannot disagree, because they are the same array. A caller that adds
       * a type gets the sentence for free and cannot forget to update it.
       *
       * ⚠️⚠️ **ONE ENTRY PER FORMAT, WHICH IT WAS NOT.** An `accept` list names a format twice on
       * purpose — `.txt` AND `text/plain` — because a browser matches on either and the operating
       * system's idea of a `.md` varies by machine. Printing the array gave
       * `.TXT · .MD · … · TXT · MARKDOWN · …`: every format listed twice, in two spellings, in two
       * blocks. The list is right; the sentence was reading it as if each entry were a format.
       */}
      <p className="text-muted-foreground text-[11px]">{formatsIn(accept).join(' · ')}</p>

      {/* ⚠️ A button, not an anchor: it opens a dialogue rather than going anywhere, and an `<a>`
          with no href is a link a keyboard cannot reach. */}
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => {
          picker.current?.click();
        }}
        className="text-primary text-[12px] disabled:cursor-not-allowed"
      >
        {t('upload.browse')}
      </button>
    </div>
  );
}

/**
 * ⚠️⚠️ **THE OFFICE MIME TYPES ARE NOT SUBTYPES ANYBODY CAN READ.** The subtype rule below is right
 * for `image/png` and `application/pdf`; for a `.docx` it produces
 * `VND.OPENXMLFORMATS-OFFICEDOCUMENT.WORDPROCESSINGML.DOCUMENT`, which ran off the side of the leave
 * detail's attachment box. These four are the ones this product accepts, named the way the file is
 * named on somebody's disk.
 */
const SHORT_FORMATS: Readonly<Record<string, string>> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/msword': 'DOC',
  'application/vnd.ms-excel': 'XLS',
  'text/csv': 'CSV',
  'text/plain': 'TXT',
};

/**
 * ⚠️ **Two spellings of one format.** `.jpg` and `.jpeg` are the same picture, and `text/markdown`
 * is the same file as `.md`; both belong in `accept`, and neither should be said twice to a reader
 * deciding whether their file will be taken.
 */
const ALIASES: Readonly<Record<string, string>> = { JPEG: 'JPG', MARKDOWN: 'MD' };

/**
 * The distinct formats an `accept` list will take, in the order they first appear.
 *
 * ⚠️ **Order preserved rather than sorted**, because the caller groups its list by kind — text,
 * documents, pictures — and alphabetising it would scatter that grouping for no gain.
 */
function formatsIn(accept: readonly string[]): string[] {
  const seen = new Set<string>();
  const formats: string[] = [];

  for (const entry of accept) {
    const format = ALIASES[shortFormat(entry)] ?? shortFormat(entry);
    if (seen.has(format)) continue;
    seen.add(format);
    formats.push(format);
  }

  return formats;
}

/**
 * ⚠️ **The subtype, upper-cased** — `image/svg+xml` reads as `SVG`, which is what somebody looking
 * at their own file has in front of them. The MIME string is our vocabulary, not theirs. Anything
 * whose subtype is itself unreadable is named in the map above instead.
 *
 * ⚠️ **An extension arrives with its dot and leaves without one**, so `.txt` and `text/plain` both
 * become `TXT` and `formatsIn` can see that they are the same thing.
 */
function shortFormat(type: string): string {
  if (type.startsWith('.')) return type.slice(1).toUpperCase();
  const known = SHORT_FORMATS[type.toLowerCase()];
  if (known !== undefined) return known;
  return (type.split('/')[1] ?? type).split('+')[0]?.toUpperCase() ?? type;
}
